import { useUser } from '@clerk/react';
import { Link } from '@tanstack/react-router';
import { usePostHog } from 'posthog-js/react';
import { useEffect, useId, useRef, useState } from 'react';

import { AppSumoFeaturePicture } from '@/components/appsumo/appsumo-feature-picture.component';
import { Button } from '@/components/ui/button';
import { useAppSumoUpgrade } from '@/hooks/use-appsumo-upgrade.hook';
import {
  appSumoUpgradeSearch,
  HOME_SHOWCASE,
  trackAppSumoUpgrade,
} from '@/lib/appsumo-upgrade';
import { isDismissedWithin, stampDismissal } from '@/lib/timed-dismissal';

/** Hidden per user for a week, then back while the price is open. */
const HIDE_KEY_PREFIX = 'cl_appsumo_home_hidden_';
export const APPSUMO_HOME_HIDE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const readHidden = (userId: string | undefined) =>
  !!userId &&
  isDismissedWithin(`${HIDE_KEY_PREFIX}${userId}`, APPSUMO_HOME_HIDE_TTL_MS);

const writeHidden = (userId: string) =>
  stampDismissal(`${HIDE_KEY_PREFIX}${userId}`);

/**
 * Above the classic home of an AppSumo-only workspace, while the upgrade
 * price is open. That home is all such a workspace ever sees, so this is
 * where it meets what Cleanlist does now: Clu at work, agents (a Pro thing,
 * tagged so), Sequences, and the way to the upgrade page. It can be hidden
 * for a week. Nothing here renders for anyone else (the offer is only shown
 * to AppSumo-only orgs).
 */
export function AppSumoHomeShowcase() {
  const posthog = usePostHog();
  const { user } = useUser();
  const userId = user?.id;
  const { offer, prices, active, phase } = useAppSumoUpgrade();
  const titleId = useId();

  const [hidden, setHidden] = useState(() => readHidden(userId));
  useEffect(() => {
    setHidden(readHidden(userId));
  }, [userId]);

  const visible = active && !!offer && !!prices && !hidden;
  const tier = offer?.appsumo_tier ?? null;

  // Once per home visit it is shown on.
  const viewed = useRef(false);
  useEffect(() => {
    if (!visible || viewed.current) return;
    viewed.current = true;
    trackAppSumoUpgrade(posthog, 'appsumo_upgrade_home_viewed', {
      tier,
      phase,
    });
  }, [visible, tier, phase, posthog]);

  if (!visible || !offer || !prices) return null;

  const hide = () => {
    if (userId) writeHidden(userId);
    setHidden(true);
    trackAppSumoUpgrade(posthog, 'appsumo_upgrade_home_hidden', {
      tier,
      phase,
    });
  };

  return (
    <div className='px-4 pt-4 lg:px-6 lg:pt-6'>
      <section
        aria-labelledby={titleId}
        className='mx-auto w-full max-w-[960px] rounded-2xl border border-border bg-background p-5 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_8px_24px_-12px_rgba(16,24,40,0.10)] sm:p-6 dark:shadow-[0_0_0_1px_rgba(255,255,255,0.02)]'
      >
        <h2
          id={titleId}
          className='text-lg font-semibold tracking-tight text-foreground'
        >
          {HOME_SHOWCASE.title}
        </h2>
        <p className='mt-1 max-w-[640px] text-[13px] leading-relaxed text-muted-foreground'>
          {HOME_SHOWCASE.line(prices, offer.ends_at)}
        </p>

        <ul className='mt-5 grid grid-cols-1 gap-3 md:grid-cols-3'>
          {HOME_SHOWCASE.cards.map((card) => (
            <li
              key={card.key}
              className='flex flex-col rounded-xl border border-border bg-card p-3'
            >
              <AppSumoFeaturePicture gain={card.key} className='rounded-lg' />
              <div className='mt-3 flex flex-wrap items-center gap-2'>
                <h3 className='text-sm font-semibold text-foreground'>
                  {card.title}
                </h3>
                {'tag' in card ? (
                  <span className='rounded-full bg-primary/10 px-2 py-0.5 text-[11px] leading-4 font-semibold text-primary'>
                    {card.tag}
                  </span>
                ) : null}
              </div>
              <p className='mt-1 text-[13px] leading-relaxed text-muted-foreground'>
                {card.line}
              </p>
            </li>
          ))}
        </ul>

        <div className='mt-5 flex flex-wrap items-center gap-2'>
          <Button asChild className='h-10 px-5'>
            <Link
              to='/upgrade'
              search={appSumoUpgradeSearch({ source: 'home' })}
              onClick={() =>
                trackAppSumoUpgrade(posthog, 'appsumo_upgrade_home_clicked', {
                  tier,
                  phase,
                })
              }
            >
              {HOME_SHOWCASE.primary}
            </Link>
          </Button>
          <Button
            type='button'
            variant='ghost'
            onClick={hide}
            className='h-10 !rounded-[8px] px-4 text-muted-foreground hover:text-foreground'
          >
            {HOME_SHOWCASE.hide}
          </Button>
        </div>
      </section>
    </div>
  );
}
